import { CheckIcon } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  DossbotEventType,
  usePushDossbotEventSubscription,
} from '~/common/generated/apollo-operations'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import {
  ChainOfThought,
  ChainOfThoughtContent,
  ChainOfThoughtHeader,
  ChainOfThoughtSearchResult,
  ChainOfThoughtSearchResults,
  ChainOfThoughtStep,
} from '~/components/ai-elements/chain-of-thought'
import { AnimatedDossbotIcon } from '~/components/dossbot/AnimatedDossbotIcon'
import { ProgressEvent, ResultMessage } from '~/components/dossbot/DossbotChat'
import { useProcessEvent } from '~/components/dossbot/eventProcessingUtils'
import { ToolConfirmation } from './ToolConfirmation'
import { DetailedToolRunStep } from './types'

interface DossbotChatEventProps {
  chatId: string
  onAddMessage: (message: ResultMessage) => void
  initialPendingConfirmations: DetailedToolRunStep[]
  progressEvents: ProgressEvent[]
  setProgressEvents: React.Dispatch<React.SetStateAction<ProgressEvent[]>>
  eventTitle: { title: string; icon: JSX.Element }
  setEventTitle: React.Dispatch<React.SetStateAction<{ title: string; icon: JSX.Element }>>
}

export type EventHeader = { title: string; icon: JSX.Element }

/**
 * DossbotChatEvent component that displays the progress events for a given chat
 */
export function DossbotChatEvent({
  chatId,
  onAddMessage,
  initialPendingConfirmations,
  progressEvents,
  setProgressEvents,
  eventTitle,
  setEventTitle,
}: DossbotChatEventProps) {
  const { t } = useTypedTranslation()
  const processEvent = useProcessEvent()
  const PENDING_CONFIRMATION_TITLE = useMemo(() => t('dossbot.waiting_for_confirmation'), [t])

  // Track pending tool run confirmations
  const [pendingConfirmations, setPendingConfirmations] = useState<DetailedToolRunStep[]>(
    initialPendingConfirmations,
  )

  // Store the last  event title (not overridden by pending confirmations)
  const lastEventHeaderRef = useRef<EventHeader>(eventTitle)

  useEffect(() => {
    setPendingConfirmations(initialPendingConfirmations)
  }, [initialPendingConfirmations])

  useEffect(() => {
    if (eventTitle.title !== PENDING_CONFIRMATION_TITLE) {
      lastEventHeaderRef.current = eventTitle
    }
  }, [eventTitle, PENDING_CONFIRMATION_TITLE])

  // Update event title based on pending confirmations
  useEffect(() => {
    if (pendingConfirmations.length > 0) {
      setEventTitle({
        title: PENDING_CONFIRMATION_TITLE,
        icon: <AnimatedDossbotIcon height={16} width={16} />,
      })
    } else {
      setEventTitle(lastEventHeaderRef.current)
    }
  }, [pendingConfirmations.length, setEventTitle, PENDING_CONFIRMATION_TITLE])

  // Add a progress event to the list
  const addProgressEvent = (progressEvent: ProgressEvent) => {
    setProgressEvents((prev) => [...prev, progressEvent])
  }

  const addPendingConfirmation = (toolRunStep: DetailedToolRunStep) => {
    setPendingConfirmations((prev) => [...prev, toolRunStep])
  }
  const removePendingConfirmation = (toolRunStepId: string) => {
    setPendingConfirmations((prev) => prev.filter((t) => t.id !== toolRunStepId))
  }

  usePushDossbotEventSubscription({
    variables: { chatId },
    onData: ({ data }) => {
      if (!data.data) {
        return
      }

      const event = data.data.pushDossbotEvent

      // Special handling for CHAT_RUN_COMPLETED with response
      if (event.type === DossbotEventType.CHAT_RUN_COMPLETED && event.chatRun?.response) {
        const message: ResultMessage = {
          role: 'assistant',
          message: { type: 'text', text: event.chatRun.response },
        }
        onAddMessage(message)

        // Compute updated events: remove last if AGENT_RUN_COMPLETED, then remove first if only CHAT_RUN_STARTED remains
        const updatedEvents = (() => {
          const withoutLastIfAgentRunCompleted =
            progressEvents[progressEvents.length - 1]?.type === DossbotEventType.AGENT_RUN_COMPLETED
              ? progressEvents.slice(0, -1)
              : progressEvents

          // Remove the start event if it's the only event and is CHAT_RUN_STARTED
          const withoutStartIfOnly =
            withoutLastIfAgentRunCompleted.length === 1 &&
            withoutLastIfAgentRunCompleted[0]?.type === DossbotEventType.CHAT_RUN_STARTED
              ? withoutLastIfAgentRunCompleted.slice(0, -1)
              : withoutLastIfAgentRunCompleted

          return withoutStartIfOnly
        })()

        setProgressEvents(updatedEvents)

        const completedTitle = { title: t('dossbot.completed'), icon: <CheckIcon size={16} /> }
        lastEventHeaderRef.current = completedTitle
        if (pendingConfirmations.length === 0) {
          setEventTitle(completedTitle)
        }
        return
      }

      // Special handling for CHAT_RUN_FAILED
      if (event.type === DossbotEventType.CHAT_RUN_FAILED) {
        const message: ResultMessage = {
          role: 'assistant',
          message: { type: 'text', text: t('dossbot.error_message') },
        }
        onAddMessage(message)
        return
      }

      // Special handling for CHAT_RUN_CANCELED
      if (event.type === DossbotEventType.CHAT_RUN_CANCELED) {
        const message: ResultMessage = {
          role: 'assistant',
          message: {
            type: 'text',
            text: t('dossbot.canceled_message'),
          },
        }
        onAddMessage(message)
        return
      }

      // Use shared event processing utility
      const result = processEvent({
        eventType: event.type,
        eventPayload: event.payload,
        chatRun: event.chatRun,
        agentRun: event.agentRun,
        toolRunStep: event.toolRunStep,
      })

      // Add progress event if one was returned
      if (result.progressEvent) {
        addProgressEvent(result.progressEvent)
      }

      // Handle pending confirmations first to avoid race conditions
      if (result.shouldAddPendingConfirmation && event.toolRunStep) {
        addPendingConfirmation(event.toolRunStep)
      }

      // Update event title if one was returned
      if (result.eventTitle) {
        lastEventHeaderRef.current = result.eventTitle
        if (pendingConfirmations.length === 0 && !result.shouldAddPendingConfirmation) {
          setEventTitle(result.eventTitle)
        }
      }

      // Update event title with custom function if provided
      if (result.updateEventTitle) {
        const updatedTitle = result.updateEventTitle(eventTitle)
        lastEventHeaderRef.current = updatedTitle
        if (pendingConfirmations.length === 0 && !result.shouldAddPendingConfirmation) {
          setEventTitle(updatedTitle)
        }
      }

      if (result.shouldRemovePendingConfirmation && event.toolRunStep) {
        removePendingConfirmation(event.toolRunStep.id)
      }
    },
  })

  return (
    <>
      {/* Render progress events */}
      {progressEvents.length > 0 && (
        <ChainOfThought className="ps-2" defaultOpen={true}>
          <ChainOfThoughtHeader className="flex items-center gap-2">
            {eventTitle.icon}
            <span className="flex-1 text-left text-gray-9">{eventTitle.title}</span>
          </ChainOfThoughtHeader>
          <ChainOfThoughtContent>
            {progressEvents.map((thought, index) => (
              <ChainOfThoughtStep
                key={index}
                icon={thought.icon}
                label={thought.title}
                status="complete"
              >
                {thought.result && (
                  <ChainOfThoughtSearchResults>
                    <ChainOfThoughtSearchResult className="text-gray-9">
                      {thought.result}
                    </ChainOfThoughtSearchResult>
                  </ChainOfThoughtSearchResults>
                )}
              </ChainOfThoughtStep>
            ))}
          </ChainOfThoughtContent>
        </ChainOfThought>
      )}

      {/* Render pending tool confirmations */}
      {pendingConfirmations.length > 0 && (
        <div className="my-4 space-y-2">
          {pendingConfirmations.map((toolRunStep) => (
            <ToolConfirmation key={toolRunStep.id} chatId={chatId} toolRunStep={toolRunStep} />
          ))}
        </div>
      )}
    </>
  )
}
